import { useEffect, useRef } from "react";
import { scriptsCollection } from "@/lib/collections/scripts-collection";
import { pagesCollection } from "@/lib/collections/pages-collection";
import { httpStore } from "@/lib/api/instance";
import { StoreAuthException } from "@/lib/api/errors";
import { getQueue } from "@/lib/queue-store";
import { shouldHandleFlush } from "@/lib/flush-request";
import { queueHealth } from "@/lib/queue-health";
import { settings } from "@/lib/settings";

const BEAT_MS = 20000;

/** Tells the server what this device is still holding, and acts on a flush
 * the desk has requested.
 *
 * Unsent pages live in this browser's own storage, so before this existed
 * the desk could not tell a phone sitting on 200 pages from one that had
 * finished for the day — the only signal was the absence of uploads, which
 * looks identical either way. Now every device reports its own queue, and
 * the reply carries the desk's "push everything now" back down, so a
 * stalled phone can be restarted without finding the person holding it.
 *
 * Reads the collections directly rather than taking props: this runs above
 * both capture screens, and neither one should have to thread a device
 * report through its own render. */
export function useDeviceHeartbeat(): void {
  const scannerName = settings.getScannerName();

  /** Guards against two beats overlapping on a slow network, which would
   * otherwise let a flush be handled twice. */
  const beating = useRef(false);

  useEffect(() => {
    if (!scannerName) return;

    async function beat() {
      if (document.hidden || beating.current) return;
      beating.current = true;
      try {
        const queue = getQueue();
        const pages = pagesCollection.toArray;
        const health = queueHealth(scriptsCollection.toArray, pages);
        const reply = await httpStore.heartbeat({
          deviceId: settings.getDeviceId(),
          scannerName: scannerName!,
          waiting: health.waiting,
          blocked: health.blocked,
          needsRetake: health.needsRetake,
          maxAttempts: health.maxAttempts,
          lastFailReason: health.lastFailReason,
          pauseReason: queue?.pauseReason ?? null,
          oldestWaitingAt:
            health.oldestWaitingAt === null ? null : new Date(health.oldestWaitingAt).toISOString(),
        });

        const requested = reply.flushRequestedAt;
        if (shouldHandleFlush(requested, settings.getFlushHandledAt())) {
          settings.setFlushHandledAt(requested!);
          await queue?.forceUploadAll(pages);
        }
      } catch (e) {
        // A failed beat is a no-op; the next one is 20s away. An auth
        // failure is worth acting on though — it means this device's token
        // is no longer accepted, which is also why its uploads are failing.
        if (e instanceof StoreAuthException) getQueue()?.pause("auth");
      } finally {
        beating.current = false;
      }
    }

    void beat();
    const onVisible = () => {
      if (!document.hidden) void beat();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => void beat(), BEAT_MS);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // Nothing else belongs in here: every number the beat sends is read off
    // the collections at send time, so a page change must not restart the
    // timer — on a busy capture screen that would mean a beat per photo.
  }, [scannerName]);
}
